import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../utils/api'
import { Plus, Edit2, Trash2, Check, X, ChevronDown, ChevronRight, Building2, MapPin, Users, DoorOpen, Wrench, ImageIcon, Tag, CircleDot, FolderOpen, Globe, Copy, GripVertical } from 'lucide-react'
type TabKey = 'companies'|'rooms'|'brands'|'vendors'|'ma-templates'|'asset-types'|'asset-statuses'|'asset-categories'

interface SimpleItem { id: string; name: string; department?: string; building?: string; floor?: string; location?: string; notes?: string; isGlobal?: boolean }
interface RoomItem { id: string; name: string; building?: string; floor?: string; siteId?: string; site?: {id:string;name:string} }
interface SiteItem { id: string; name: string; location?: string; notes?: string; company?: {id:string;name:string} }
interface CompanyItem { id: string; name: string; code?: string; address?: string; phone?: string; email?: string; notes?: string; logo?: string }

function getCurrentUser(): { role: string } | null {
  try { const u = localStorage.getItem('user'); return u ? JSON.parse(u) : null } catch { return null }
}

// ─── Global Badge ─────────────────────────────────────────────
function GlobalBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-xs bg-purple-50 text-purple-600 border border-purple-200 px-1.5 py-0.5 rounded-full flex-shrink-0">
      <Globe size={9}/> ค่าเริ่มต้น
    </span>
  )
}

// ─── Company Picker (SUPER_ADMIN) ─────────────────────────────
interface CompanyPickerProps {
  companies: CompanyItem[]
  selected: string | null
  onChange: (id: string | null) => void
}

function CompanyPicker({ companies, selected, onChange }: CompanyPickerProps) {
  const [open, setOpen] = useState(false)
  const active = selected ? companies.find(c => c.id === selected) : null

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm hover:border-gray-300 transition-colors shadow-sm"
      >
        {selected ? <Building2 size={14} className="text-blue-500"/> : <Globe size={14} className="text-purple-500"/>}
        <span className="font-medium text-gray-700">
          {active ? active.name : 'ค่าเริ่มต้น (Global)'}
        </span>
        {active?.code && <span className="text-xs text-gray-400 font-mono">{active.code}</span>}
        <ChevronDown size={13} className="text-gray-400 ml-1"/>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 min-w-56 overflow-hidden">
          <div className="p-1">
            <button
              onClick={() => { onChange(null); setOpen(false) }}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${selected === null ? 'bg-purple-50 text-purple-700' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              <Globe size={13}/> ค่าเริ่มต้น (Global)
            </button>
            {companies.length > 0 && (
              <div className="border-t border-gray-100 mt-1 pt-1">
                <p className="text-xs text-gray-400 px-3 py-1">บริษัท</p>
                {companies.map(c => (
                  <button
                    key={c.id}
                    onClick={() => { onChange(c.id); setOpen(false) }}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${selected === c.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
                  >
                    <Building2 size={13}/>
                    <span className="flex-1 text-left">{c.name}</span>
                    {c.code && <span className="text-xs text-gray-400 font-mono">{c.code}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Asset Seed Defaults Banner ───────────────────────────────
function AssetSeedBanner({ companyId, companyName, onDone }: { companyId: string; companyName: string; onDone: () => void }) {
  const [loading, setLoading] = useState(false)

  const seed = async () => {
    setLoading(true)
    try {
      const res = await api.post('/masterdata/seed-asset-defaults', { companyId })
      const { typesAdded, statusesAdded, categoriesAdded, brandsAdded } = res.data
      alert(`คัดลอกสำเร็จ: ประเภท ${typesAdded}, สถานะ ${statusesAdded}, หมวดหมู่ ${categoriesAdded}, ยี่ห้อ ${brandsAdded} รายการ`)
      onDone()
    } catch (e: any) { alert(e.response?.data?.message || e.message) }
    finally { setLoading(false) }
  }

  return (
    <div className="flex items-center justify-between bg-purple-50 border border-purple-200 rounded-xl px-4 py-3">
      <div className="flex items-center gap-2">
        <Copy size={15} className="text-purple-500"/>
        <p className="text-sm text-purple-800">
          คัดลอกค่าเริ่มต้น (Global) ไปยัง <strong>{companyName}</strong>
        </p>
      </div>
      <button
        onClick={seed}
        disabled={loading}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700 disabled:opacity-50"
      >
        <Copy size={12}/> {loading ? 'กำลังคัดลอก...' : 'คัดลอก'}
      </button>
    </div>
  )
}

function SimpleList({ endpoint, label, fields, isAdmin = true, isSuperAdmin = false, targetCompanyId, apiPath }: {
  endpoint: string
  label: string
  fields?: {key:string;placeholder:string;label:string}[]
  isAdmin?: boolean
  isSuperAdmin?: boolean
  targetCompanyId?: string | null
  apiPath?: string // override the REST path (default: `/masterdata/${endpoint}`) — e.g. Vendor has its own dedicated `/vendors` API
}) {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState<Record<string,string>>({ name:'' })
  const [editId, setEditId] = useState<string|null>(null)
  const [editForm, setEditForm] = useState<Record<string,string>>({})
  const basePath = apiPath || `/masterdata/${endpoint}`

  const params: Record<string, string> = isSuperAdmin && targetCompanyId !== undefined
    ? { targetCompanyId: targetCompanyId ?? '' }
    : {}

  const { data = [] } = useQuery<SimpleItem[]>({
    queryKey: [endpoint, targetCompanyId],
    queryFn: () => api.get(basePath, { params }).then(r=>r.data),
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: [endpoint] })

  const createMut = useMutation({
    mutationFn: (d: any) => {
      const body = isSuperAdmin && targetCompanyId !== undefined
        ? { ...d, targetCompanyId: targetCompanyId ?? '' }
        : d
      return api.post(basePath, body)
    },
    onSuccess: () => { invalidate(); setAdding(false); setForm({name:''}) },
    onError: (e:any) => alert(e.response?.data?.message || 'เพิ่มไม่สำเร็จ'),
  })
  const updateMut = useMutation({
    mutationFn: ({id,...d}: any) => api.put(`${basePath}/${id}`, d),
    onSuccess: () => { invalidate(); setEditId(null) },
    onError: (e:any) => alert(e.response?.data?.message || 'บันทึกไม่สำเร็จ'),
  })
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`${basePath}/${id}`),
    onSuccess: () => invalidate(),
    onError: (e:any) => alert(e.response?.data?.message || 'ลบไม่สำเร็จ'),
  })

  const allFields = [{ key:'name', placeholder: `ชื่อ ${label}...`, label:'ชื่อ' }, ...(fields||[])]
  const ic = "border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"

  const isGlobalMode = isSuperAdmin && targetCompanyId === null
  const ownItems = (data as any[]).filter(item => !item.isGlobal)
  const globalItems = (data as any[]).filter(item => item.isGlobal)

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="font-semibold text-gray-700 flex items-center gap-2">
          {label}
          {isGlobalMode && <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">ค่าเริ่มต้น</span>}
        </div>
        {isAdmin && <button onClick={()=>{setAdding(!adding)}} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"><Plus size={14}/> เพิ่ม</button>}
      </div>
      {adding && (
        <div className="bg-blue-50 rounded-xl p-3 flex flex-wrap gap-2 items-center">
          {allFields.map(f=><input key={f.key} value={form[f.key]||''} onChange={e=>setForm(p=>({...p,[f.key]:e.target.value}))} className={ic} placeholder={f.placeholder}/>)}
          <button onClick={()=>createMut.mutate(form)} className={ic + ' bg-blue-600 text-white border-blue-600'}><Check size={14}/></button>
          <button onClick={()=>{setAdding(false);setForm({name:''})}} className={ic}><X size={14}/></button>
        </div>
      )}

      {/* Global edit mode */}
      {isGlobalMode && (
        <>
          {data.length === 0 && !adding && <div className="text-center text-gray-400 py-6 text-sm">ยังไม่มีข้อมูล</div>}
          {(data as any[]).map((item:any) => editId === item.id ? (
            <div key={item.id} className="bg-yellow-50 rounded-xl p-3 flex flex-wrap gap-2 items-center">
              {allFields.map(f=><input key={f.key} value={editForm[f.key]||''} onChange={e=>setEditForm(p=>({...p,[f.key]:e.target.value}))} className={ic} placeholder={f.placeholder}/>)}
              <button onClick={()=>updateMut.mutate({id:item.id,...editForm})} className={ic + ' bg-green-600 text-white border-green-600'}><Check size={14}/></button>
              <button onClick={()=>setEditId(null)} className={ic}><X size={14}/></button>
            </div>
          ) : (
            <div key={item.id} className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-2.5 flex items-center gap-3">
              <Globe size={12} className="text-purple-400 flex-shrink-0"/>
              <span className="flex-1 font-medium text-gray-800 text-sm">{item.name}</span>
              {item.location && <span className="text-xs text-gray-400"><MapPin size={10} className="inline"/> {item.location}</span>}
              {allFields.slice(1).filter(f=>f.key!=='location').map(f=>item[f.key] ? <span key={f.key} className="text-xs text-gray-400">{item[f.key]}</span> : null)}
              {isAdmin && <button onClick={()=>{setEditId(item.id);const ef:any={};allFields.forEach(f=>ef[f.key]=item[f.key]||'');setEditForm(ef)}} className="p-1 text-gray-400 hover:text-blue-500"><Edit2 size={14}/></button>}
              {isAdmin && <button onClick={()=>deleteMut.mutate(item.id)} className="p-1 text-gray-400 hover:text-red-500"><Trash2 size={14}/></button>}
            </div>
          ))}
        </>
      )}

      {/* Company/normal view: own items editable, globals read-only */}
      {!isGlobalMode && (
        <>
          {ownItems.length === 0 && globalItems.length === 0 && !adding && <div className="text-center text-gray-400 py-6 text-sm">ยังไม่มีข้อมูล</div>}
          {ownItems.map((item:any) => editId === item.id ? (
            <div key={item.id} className="bg-yellow-50 rounded-xl p-3 flex flex-wrap gap-2 items-center">
              {allFields.map(f=><input key={f.key} value={editForm[f.key]||''} onChange={e=>setEditForm(p=>({...p,[f.key]:e.target.value}))} className={ic} placeholder={f.placeholder}/>)}
              <button onClick={()=>updateMut.mutate({id:item.id,...editForm})} className={ic + ' bg-green-600 text-white border-green-600'}><Check size={14}/></button>
              <button onClick={()=>setEditId(null)} className={ic}><X size={14}/></button>
            </div>
          ) : (
            <div key={item.id} className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-2.5 flex items-center gap-3">
              <span className="flex-1 font-medium text-gray-800 text-sm">{item.name}</span>
              {item.location && <span className="text-xs text-gray-400"><MapPin size={10} className="inline"/> {item.location}</span>}
              {allFields.slice(1).filter(f=>f.key!=='location').map(f=>item[f.key] ? <span key={f.key} className="text-xs text-gray-400">{item[f.key]}</span> : null)}
              {isAdmin && <button onClick={()=>{setEditId(item.id);const ef:any={};allFields.forEach(f=>ef[f.key]=item[f.key]||'');setEditForm(ef)}} className="p-1 text-gray-400 hover:text-blue-500"><Edit2 size={14}/></button>}
              {isAdmin && <button onClick={()=>deleteMut.mutate(item.id)} className="p-1 text-gray-400 hover:text-red-500"><Trash2 size={14}/></button>}
            </div>
          ))}
          {globalItems.length > 0 && (
            <>
              {ownItems.length > 0 && <div className="px-2 py-1.5"><p className="text-xs text-gray-400 font-medium">ค่าเริ่มต้นระบบ (อ่านอย่างเดียว)</p></div>}
              {globalItems.map((item:any) => (
                <div key={item.id} className="bg-purple-50/30 rounded-xl border border-purple-100 px-4 py-2.5 flex items-center gap-3 opacity-80">
                  <span className="flex-1 font-medium text-gray-700 text-sm">{item.name}</span>
                  {item.location && <span className="text-xs text-gray-400"><MapPin size={10} className="inline"/> {item.location}</span>}
                  <GlobalBadge/>
                </div>
              ))}
            </>
          )}
        </>
      )}
    </div>
  )
}

// ─── Company Manager Component (hierarchy) ─────────────────────────────────────
function CompanyManager({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient()
  const logoInputRef = useRef<HTMLInputElement>(null)
  const [logoUploadId, setLogoUploadId] = useState<string|null>(null)
  const [expandedCompany, setExpandedCompany] = useState<string|null>(null)
  const [expandedSite, setExpandedSite] = useState<string|null>(null)
  const [addingCompany, setAddingCompany] = useState(false)
  const [addingSite, setAddingSite] = useState<string|null>(null) // companyId
  const [addingDept, setAddingDept] = useState<string|null>(null)  // siteId
  const [addingRoom, setAddingRoom] = useState<string|null>(null)
  const [editTarget, setEditTarget] = useState<{type:string;id:string}|null>(null)
  const [form, setForm] = useState<Record<string,string>>({})

  const { data: companies = [] } = useQuery<CompanyItem[]>({ queryKey: ['companies'], queryFn: ()=>api.get('/masterdata/companies').then(r=>r.data) })
  const { data: sites = [] } = useQuery<SiteItem[]>({ queryKey: ['sites'], queryFn: ()=>api.get('/masterdata/sites').then(r=>r.data) })
  const { data: depts = [] } = useQuery<SimpleItem[]>({ queryKey: ['departments'], queryFn: ()=>api.get('/masterdata/departments').then(r=>r.data) })
  const { data: rooms = [] } = useQuery<RoomItem[]>({ queryKey: ['rooms'], queryFn: ()=>api.get('/masterdata/rooms').then(r=>r.data) })

  const ic = "border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"

  const uploadLogo = async (companyId: string, file: File) => {
    const fd = new FormData(); fd.append('logo', file)
    await api.post(`/masterdata/companies/${companyId}/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    qc.invalidateQueries({ queryKey: ['companies'] })
  }

  const createCompany = useMutation({ mutationFn: (d:any)=>api.post('/masterdata/companies',d), onSuccess: ()=>{qc.invalidateQueries({queryKey:['companies']});setAddingCompany(false);setForm({})} })
  const updateCompany = useMutation({ mutationFn: ({id,...d}:any)=>api.put(`/masterdata/companies/${id}`,d), onSuccess: ()=>{qc.invalidateQueries({queryKey:['companies']});setEditTarget(null)} })
  const deleteCompany = useMutation({ mutationFn: (id:string)=>api.delete(`/masterdata/companies/${id}`), onSuccess: ()=>qc.invalidateQueries({queryKey:['companies']}) })

  const createSite = useMutation({ mutationFn: (d:any)=>api.post('/masterdata/sites',d), onSuccess: ()=>{qc.invalidateQueries({queryKey:['sites']});setAddingSite(null);setForm({})} })
  const updateSite = useMutation({ mutationFn: ({id,...d}:any)=>api.put(`/masterdata/sites/${id}`,d), onSuccess: ()=>{qc.invalidateQueries({queryKey:['sites']});setEditTarget(null)} })
  const deleteSite = useMutation({ mutationFn: (id:string)=>api.delete(`/masterdata/sites/${id}`), onSuccess: ()=>qc.invalidateQueries({queryKey:['sites']}) })

  const createDept = useMutation({ mutationFn: (d:any)=>api.post('/masterdata/departments',d), onSuccess: ()=>{qc.invalidateQueries({queryKey:['departments']});setAddingDept(null);setForm({})} })
  const updateDept = useMutation({ mutationFn: ({id,...d}:any)=>api.put(`/masterdata/departments/${id}`,d), onSuccess: ()=>{qc.invalidateQueries({queryKey:['departments']});setEditTarget(null)} })
  const deleteDept = useMutation({ mutationFn: (id:string)=>api.delete(`/masterdata/departments/${id}`), onSuccess: ()=>qc.invalidateQueries({queryKey:['departments']}) })

  const createRoom = useMutation({ mutationFn: (d:any)=>api.post('/masterdata/rooms',d), onSuccess: ()=>{qc.invalidateQueries({queryKey:['rooms']});setAddingRoom(null);setForm({})} })
  const updateRoom = useMutation({ mutationFn: ({id,...d}:any)=>api.put(`/masterdata/rooms/${id}`,d), onSuccess: ()=>{qc.invalidateQueries({queryKey:['rooms']});setEditTarget(null)} })
  const deleteRoom = useMutation({ mutationFn: (id:string)=>api.delete(`/masterdata/rooms/${id}`), onSuccess: ()=>qc.invalidateQueries({queryKey:['rooms']}) })

  const companySites = (cid:string) => sites.filter((s:any)=>s.company?.id===cid || s.companyId===cid)
  const siteDepts = (sid:string) => (depts as any[]).filter((d:any)=>d.site?.id===sid || d.siteId===sid)
  const siteRooms = (sid:string) => rooms.filter((r:any)=>r.site?.id===sid||r.siteId===sid)

  return (
    <div className="space-y-3">
      {/* Hidden logo file input */}
      <input ref={logoInputRef} type="file" accept="image/*" className="hidden"
        onChange={async e => {
          if (e.target.files?.[0] && logoUploadId) {
            await uploadLogo(logoUploadId, e.target.files[0])
            e.target.value = ''
            setLogoUploadId(null)
          }
        }}/>
      <div className="flex justify-between items-center">
        <div className="font-semibold text-gray-700 flex items-center gap-2"><Building2 size={16}/> จัดการ Company</div>
        {isAdmin && <button onClick={()=>{setAddingCompany(true);setForm({})}} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"><Plus size={14}/> เพิ่ม Company</button>}
      </div>

      {addingCompany && (
        <div className="bg-blue-50 rounded-xl p-3 flex flex-wrap gap-2 items-center">
          <input value={form.name||''} onChange={e=>setForm(p=>({...p,name:e.target.value}))} className={ic} placeholder="ชื่อบริษัท..."/>
          <input value={form.code||''} onChange={e=>setForm(p=>({...p,code:e.target.value}))} className={ic} placeholder="รหัส..."/>
          <input value={form.phone||''} onChange={e=>setForm(p=>({...p,phone:e.target.value}))} className={ic} placeholder="โทร..."/>
          <input value={form.email||''} onChange={e=>setForm(p=>({...p,email:e.target.value}))} className={ic} placeholder="Email..."/>
          <button onClick={()=>createCompany.mutate(form)} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm"><Check size={14}/></button>
          <button onClick={()=>setAddingCompany(false)} className="px-3 py-1.5 border rounded-lg text-sm"><X size={14}/></button>
        </div>
      )}

      {companies.length === 0 && !addingCompany && <div className="text-center text-gray-400 py-6 text-sm">ยังไม่มีข้อมูลบริษัท</div>}

      {companies.map((company:any) => (
        <div key={company.id} className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          {/* Company Header */}
          {editTarget?.type==='company' && editTarget.id===company.id ? (
            <div className="bg-yellow-50 p-3 flex flex-wrap gap-2 items-center">
              <input value={form.name||''} onChange={e=>setForm(p=>({...p,name:e.target.value}))} className={ic} placeholder="ชื่อบริษัท..."/>
              <input value={form.code||''} onChange={e=>setForm(p=>({...p,code:e.target.value}))} className={ic} placeholder="รหัส..."/>
              <input value={form.phone||''} onChange={e=>setForm(p=>({...p,phone:e.target.value}))} className={ic} placeholder="โทร..."/>
              <input value={form.email||''} onChange={e=>setForm(p=>({...p,email:e.target.value}))} className={ic} placeholder="Email..."/>
              <button onClick={()=>updateCompany.mutate({id:company.id,...form})} className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm"><Check size={14}/></button>
              <button onClick={()=>setEditTarget(null)} className="px-3 py-1.5 border rounded-lg text-sm"><X size={14}/></button>
            </div>
          ) : (
            <div className="bg-blue-50 px-4 py-3 flex items-center gap-3 cursor-pointer" onClick={()=>setExpandedCompany(expandedCompany===company.id?null:company.id)}>
              {expandedCompany===company.id ? <ChevronDown size={16} className="text-blue-600"/> : <ChevronRight size={16} className="text-blue-600"/>}
              {/* Logo */}
              {company.logo
                ? <img src={company.logo} alt="logo" className="w-8 h-8 object-contain rounded"/>
                : <Building2 size={16} className="text-blue-600"/>}
              <span className="font-semibold text-gray-800 flex-1">{company.name}</span>
              {company.code && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{company.code}</span>}
              {company.phone && <span className="text-xs text-gray-500">{company.phone}</span>}
              <span className="text-xs text-gray-400">{companySites(company.id).length} sites</span>
              {isAdmin && (
                <button onClick={e=>{e.stopPropagation();setLogoUploadId(company.id);setTimeout(()=>logoInputRef.current?.click(),50)}}
                  className="p-1 text-gray-400 hover:text-indigo-500" title="อัปโหลด Logo">
                  <ImageIcon size={14}/>
                </button>
              )}
              {isAdmin && <button onClick={e=>{e.stopPropagation();setEditTarget({type:'company',id:company.id});setForm({name:company.name,code:company.code||'',phone:company.phone||'',email:company.email||''})}} className="p-1 text-gray-400 hover:text-blue-500"><Edit2 size={14}/></button>}
              {isAdmin && <button onClick={e=>{e.stopPropagation();deleteCompany.mutate(company.id)}} className="p-1 text-gray-400 hover:text-red-500"><Trash2 size={14}/></button>}
            </div>
          )}

          {/* Sites under Company */}
          {expandedCompany===company.id && (
            <div className="bg-gray-50 px-4 py-3 space-y-2">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1"><MapPin size={12}/> Sites</span>
                {isAdmin && <button onClick={()=>{setAddingSite(company.id);setForm({companyId:company.id})}} className="flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"><Plus size={11}/> เพิ่ม Site</button>}
              </div>

              {addingSite===company.id && (
                <div className="bg-green-50 rounded-lg p-2 flex flex-wrap gap-2 items-center">
                  <input value={form.name||''} onChange={e=>setForm(p=>({...p,name:e.target.value}))} className={ic} placeholder="ชื่อ Site..."/>
                  <input value={form.location||''} onChange={e=>setForm(p=>({...p,location:e.target.value}))} className={ic} placeholder="ที่ตั้ง..."/>
                  <button onClick={()=>createSite.mutate({...form,companyId:company.id})} className="px-2 py-1 bg-green-600 text-white rounded text-xs"><Check size={12}/></button>
                  <button onClick={()=>setAddingSite(null)} className="px-2 py-1 border rounded text-xs"><X size={12}/></button>
                </div>
              )}

              {companySites(company.id).length===0 && addingSite!==company.id && <div className="text-xs text-gray-400 text-center py-2">ยังไม่มี Site</div>}

              {companySites(company.id).map((site:any) => (
                <div key={site.id} className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                  {/* Site Row */}
                  {editTarget?.type==='site' && editTarget.id===site.id ? (
                    <div className="bg-yellow-50 p-2 flex flex-wrap gap-2 items-center">
                      <input value={form.name||''} onChange={e=>setForm(p=>({...p,name:e.target.value}))} className={ic} placeholder="ชื่อ Site..."/>
                      <input value={form.location||''} onChange={e=>setForm(p=>({...p,location:e.target.value}))} className={ic} placeholder="ที่ตั้ง..."/>
                      <button onClick={()=>updateSite.mutate({id:site.id,...form,companyId:company.id})} className="px-2 py-1 bg-green-600 text-white rounded text-xs"><Check size={12}/></button>
                      <button onClick={()=>setEditTarget(null)} className="px-2 py-1 border rounded text-xs"><X size={12}/></button>
                    </div>
                  ) : (
                    <div className="px-3 py-2 flex items-center gap-2 cursor-pointer hover:bg-gray-50" onClick={()=>setExpandedSite(expandedSite===site.id?null:site.id)}>
                      {expandedSite===site.id ? <ChevronDown size={13} className="text-green-600"/> : <ChevronRight size={13} className="text-green-600"/>}
                      <MapPin size={13} className="text-green-600"/>
                      <span className="font-medium text-sm text-gray-700 flex-1">{site.name}</span>
                      {site.location && <span className="text-xs text-gray-400">{site.location}</span>}
                      <span className="text-xs text-gray-300">{siteDepts(site.id).length} Department · {siteRooms(site.id).length} ห้อง</span>
                      {isAdmin && <button onClick={e=>{e.stopPropagation();setEditTarget({type:'site',id:site.id});setForm({name:site.name,location:site.location||''})}} className="p-1 text-gray-300 hover:text-blue-500"><Edit2 size={12}/></button>}
                      {isAdmin && <button onClick={e=>{e.stopPropagation();deleteSite.mutate(site.id)}} className="p-1 text-gray-300 hover:text-red-500"><Trash2 size={12}/></button>}
                    </div>
                  )}

                  {/* Departments under Site */}
                  {expandedSite===site.id && (
                    <div className="bg-gray-50 px-4 py-2 space-y-1 border-t border-gray-100">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-1"><Users size={11}/> Department</span>
                        {isAdmin && <button onClick={()=>{setAddingDept(site.id);setForm({siteId:site.id})}} className="flex items-center gap-1 px-2 py-0.5 bg-purple-600 text-white rounded text-xs hover:bg-purple-700"><Plus size={10}/> เพิ่ม Department</button>}
                      </div>

                      {addingDept===site.id && (
                        <div className="bg-purple-50 rounded p-2 flex flex-wrap gap-2 items-center">
                          <input value={form.name||''} onChange={e=>setForm(p=>({...p,name:e.target.value}))} className={ic} placeholder="ชื่อ Department..."/>
                          <button onClick={()=>createDept.mutate({...form,siteId:site.id})} className="px-2 py-1 bg-purple-600 text-white rounded text-xs"><Check size={11}/></button>
                          <button onClick={()=>setAddingDept(null)} className="px-2 py-1 border rounded text-xs"><X size={11}/></button>
                        </div>
                      )}

                      {siteDepts(site.id).length===0 && addingDept!==site.id && <div className="text-xs text-gray-400 py-1">ยังไม่มี Department</div>}

                      {siteDepts(site.id).map((dept:any) => (
                        editTarget?.type==='dept' && editTarget.id===dept.id ? (
                          <div key={dept.id} className="bg-yellow-50 rounded p-2 flex gap-2 items-center">
                            <input value={form.name||''} onChange={e=>setForm(p=>({...p,name:e.target.value}))} className={ic} placeholder="ชื่อ Department..."/>
                            <button onClick={()=>updateDept.mutate({id:dept.id,...form,siteId:site.id})} className="px-2 py-1 bg-green-600 text-white rounded text-xs"><Check size={11}/></button>
                            <button onClick={()=>setEditTarget(null)} className="px-2 py-1 border rounded text-xs"><X size={11}/></button>
                          </div>
                        ) : (
                          <div key={dept.id} className="flex items-center gap-2 py-1 px-2 rounded hover:bg-white text-sm">
                            <Users size={11} className="text-purple-400"/>
                            <span className="flex-1 text-gray-600">{dept.name}</span>
                            {isAdmin && <button onClick={()=>{setEditTarget({type:'dept',id:dept.id});setForm({name:dept.name})}} className="p-0.5 text-gray-300 hover:text-blue-500"><Edit2 size={11}/></button>}
                            {isAdmin && <button onClick={()=>deleteDept.mutate(dept.id)} className="p-0.5 text-gray-300 hover:text-red-500"><Trash2 size={11}/></button>}
                          </div>
                        )
                      ))}
                    </div>
                  )}

                      {/* ห้อง */}
                      <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-100">
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-1"><DoorOpen size={11}/> ห้อง</span>
                        {isAdmin && <button onClick={()=>{setAddingRoom(site.id);setForm({siteId:site.id})}} className="flex items-center gap-1 px-2 py-0.5 bg-orange-500 text-white rounded text-xs hover:bg-orange-600"><Plus size={10}/> เพิ่มห้อง</button>}
                      </div>

                      {addingRoom===site.id && (
                        <div className="bg-orange-50 rounded p-2 flex flex-wrap gap-2 items-center">
                          <input value={form.name||''} onChange={e=>setForm(p=>({...p,name:e.target.value}))} className={ic} placeholder="ชื่อห้อง..."/>
                          <input value={form.building||''} onChange={e=>setForm(p=>({...p,building:e.target.value}))} className={ic} placeholder="อาคาร..."/>
                          <input value={form.floor||''} onChange={e=>setForm(p=>({...p,floor:e.target.value}))} className={ic} placeholder="ชั้น..."/>
                          <button onClick={()=>createRoom.mutate({...form,siteId:site.id})} className="px-2 py-1 bg-orange-500 text-white rounded text-xs"><Check size={11}/></button>
                          <button onClick={()=>setAddingRoom(null)} className="px-2 py-1 border rounded text-xs"><X size={11}/></button>
                        </div>
                      )}

                      {siteRooms(site.id).length===0 && addingRoom!==site.id && <div className="text-xs text-gray-400 py-1">ยังไม่มีห้อง</div>}

                      {siteRooms(site.id).map((room:any) => (
                        editTarget?.type==='room' && editTarget.id===room.id ? (
                          <div key={room.id} className="bg-yellow-50 rounded p-2 flex gap-2 items-center">
                            <input value={form.name||''} onChange={e=>setForm(p=>({...p,name:e.target.value}))} className={ic} placeholder="ชื่อห้อง..."/>
                            <input value={form.building||''} onChange={e=>setForm(p=>({...p,building:e.target.value}))} className={ic} placeholder="อาคาร..."/>
                            <input value={form.floor||''} onChange={e=>setForm(p=>({...p,floor:e.target.value}))} className={ic} placeholder="ชั้น..."/>
                            <button onClick={()=>updateRoom.mutate({id:room.id,...form,siteId:site.id})} className="px-2 py-1 bg-green-600 text-white rounded text-xs"><Check size={11}/></button>
                            <button onClick={()=>setEditTarget(null)} className="px-2 py-1 border rounded text-xs"><X size={11}/></button>
                          </div>
                        ) : (
                          <div key={room.id} className="flex items-center gap-2 py-1 px-2 rounded hover:bg-white text-sm">
                            <DoorOpen size={11} className="text-orange-400"/>
                            <span className="flex-1 text-gray-600">{room.name}</span>
                            {room.building && <span className="text-xs text-gray-400">{room.building}</span>}
                            {room.floor && <span className="text-xs text-gray-400">ชั้น {room.floor}</span>}
                            {isAdmin && <button onClick={()=>{setEditTarget({type:'room',id:room.id});setForm({name:room.name,building:room.building||'',floor:room.floor||''})}} className="p-0.5 text-gray-300 hover:text-blue-500"><Edit2 size={11}/></button>}
                            {isAdmin && <button onClick={()=>deleteRoom.mutate(room.id)} className="p-0.5 text-gray-300 hover:text-red-500"><Trash2 size={11}/></button>}
                          </div>
                        )
                      ))}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

interface ChecklistItem { id: string; label: string; checked: boolean }
interface MaTemplate { id: string; name: string; items: ChecklistItem[] }

function MaTemplateManager({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient()
  const [editId, setEditId] = useState<string|null>(null)
  const [editName, setEditName] = useState('')
  const [editItems, setEditItems] = useState<ChecklistItem[]>([])
  const [editNewItem, setEditNewItem] = useState('')
  const [adding, setAdding] = useState(false)
  const [newName, setNewName] = useState('')
  const [newItems, setNewItems] = useState('')
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  const { data: templates = [] } = useQuery<MaTemplate[]>({
    queryKey: ['ma-templates'],
    queryFn: () => api.get('/maintenance/templates').then(r => r.data),
  })

  const createMut = useMutation({
    mutationFn: (d: { name: string; items: ChecklistItem[] }) => api.post('/maintenance/templates', d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ma-templates'] }); setAdding(false); setNewName(''); setNewItems('') },
  })

  const updateMut = useMutation({
    mutationFn: ({ id, name, items }: { id: string; name: string; items: ChecklistItem[] }) =>
      api.put(`/maintenance/templates/${id}`, { name, items }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ma-templates'] }); setEditId(null) },
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/maintenance/templates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ma-templates'] }),
  })

  const startEdit = (t: MaTemplate) => {
    setEditId(t.id)
    setEditName(t.name)
    setEditItems(t.items.map(i => ({ ...i })))
    setEditNewItem('')
  }

  const addEditItem = () => {
    if (!editNewItem.trim()) return
    setEditItems(p => [...p, { id: Date.now().toString(), label: editNewItem.trim(), checked: false }])
    setEditNewItem('')
  }

  const moveEditItem = (from: number, to: number) => {
    if (from === to) return
    setEditItems(p => {
      const arr = [...p]
      const [moved] = arr.splice(from, 1)
      arr.splice(to, 0, moved)
      return arr
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Wrench size={15}/> MA Checklist Templates</h2>
        {isAdmin && !adding && (
          <button onClick={() => setAdding(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
            <Plus size={14}/> สร้าง Template
          </button>
        )}
      </div>

      {/* Create form */}
      {adding && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
          <p className="text-sm font-semibold text-blue-800">สร้าง Template ใหม่</p>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">ชื่อ Template</label>
            <input value={newName} onChange={e => setNewName(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="เช่น Preventive - Server"/>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">รายการ Checklist (แต่ละรายการ 1 บรรทัด)</label>
            <textarea value={newItems} onChange={e => setNewItems(e.target.value)} rows={5}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={"ทำความสะอาดภายนอก\nตรวจสอบสายไฟ\nทดสอบการทำงาน"}/>
          </div>
          <div className="flex gap-2">
            <button onClick={() => { setAdding(false); setNewName(''); setNewItems('') }}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm">ยกเลิก</button>
            <button disabled={!newName.trim() || !newItems.trim() || createMut.isPending}
              onClick={() => {
                const items = newItems.split('\n').filter(l => l.trim()).map((label, i) => ({ id: String(i+1), label: label.trim(), checked: false }))
                createMut.mutate({ name: newName.trim(), items })
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
              {createMut.isPending ? 'กำลังบันทึก...' : 'บันทึก'}
            </button>
          </div>
        </div>
      )}

      {/* Template list */}
      {templates.length === 0 && !adding && (
        <div className="bg-white rounded-xl border border-gray-100 p-10 text-center text-gray-400 text-sm">ยังไม่มี Template</div>
      )}
      {templates.map(t => (
        <div key={t.id} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {editId === t.id ? (
            // Edit mode
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">ชื่อ Template</label>
                <input value={editName} onChange={e => setEditName(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-2 block">รายการ Checklist</label>
                <div className="space-y-1.5 mb-2">
                  {editItems.map((item, idx) => (
                    <div key={item.id}
                      draggable
                      onDragStart={() => setDragIndex(idx)}
                      onDragOver={e => { e.preventDefault(); if (dragOverIndex !== idx) setDragOverIndex(idx) }}
                      onDrop={e => {
                        e.preventDefault()
                        if (dragIndex !== null) moveEditItem(dragIndex, idx)
                        setDragIndex(null); setDragOverIndex(null)
                      }}
                      onDragEnd={() => { setDragIndex(null); setDragOverIndex(null) }}
                      className={`flex items-center gap-2 rounded-lg ${dragOverIndex === idx && dragIndex !== null && dragIndex !== idx ? 'bg-blue-50 ring-1 ring-blue-200' : ''}`}>
                      <span className="text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing flex-shrink-0" title="ลากเพื่อจัดลำดับ"><GripVertical size={14}/></span>
                      <span className="text-xs text-gray-400 w-4">{idx+1}.</span>
                      <input value={item.label} onChange={e => setEditItems(p => p.map(i => i.id===item.id ? {...i,label:e.target.value} : i))}
                        className="flex-1 border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"/>
                      <button onClick={() => setEditItems(p => p.filter(i => i.id!==item.id))} className="text-gray-300 hover:text-red-400"><X size={13}/></button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input value={editNewItem} onChange={e => setEditNewItem(e.target.value)}
                    onKeyDown={e => { if(e.key==='Enter') { addEditItem(); e.preventDefault() }}}
                    placeholder="เพิ่มรายการใหม่..."
                    className="flex-1 border border-dashed border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                  <button onClick={addEditItem} className="px-3 py-1.5 bg-gray-100 rounded-lg text-sm hover:bg-gray-200"><Plus size={14}/></button>
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={() => setEditId(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">ยกเลิก</button>
                <button disabled={!editName.trim() || updateMut.isPending}
                  onClick={() => updateMut.mutate({ id: t.id, name: editName.trim(), items: editItems })}
                  className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
                  <Check size={14}/> {updateMut.isPending ? 'กำลังบันทึก...' : 'บันทึก'}
                </button>
              </div>
            </div>
          ) : (
            // View mode
            <div>
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{t.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{t.items.length} รายการ</p>
                </div>
                {isAdmin && (
                  <div className="flex gap-1">
                    <button onClick={() => startEdit(t)} className="p-1.5 hover:bg-blue-50 rounded-lg"><Edit2 size={14} className="text-blue-500"/></button>
                    <button onClick={() => { if(confirm(`ลบ template "${t.name}"?`)) deleteMut.mutate(t.id) }} className="p-1.5 hover:bg-red-50 rounded-lg"><Trash2 size={14} className="text-red-400"/></button>
                  </div>
                )}
              </div>
              <div className="px-4 py-3 grid grid-cols-2 gap-x-6 gap-y-1">
                {t.items.map((item, idx) => (
                  <div key={item.id} className="flex items-center gap-2 text-xs text-gray-600 py-0.5">
                    <span className="w-4 h-4 rounded bg-gray-100 text-gray-400 flex items-center justify-center text-xs flex-shrink-0">{idx+1}</span>
                    {item.label}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ─── Lookup Manager for asset-types / asset-statuses / asset-categories ───────
interface LookupItem { id: string; label?: string; name?: string; value?: string; order?: number; isGlobal?: boolean }

function LookupManager({ endpoint, label, icon, hasValue = false, isAdmin, isSuperAdmin = false, targetCompanyId }: {
  endpoint: string
  label: string
  icon: React.ReactNode
  hasValue?: boolean
  isAdmin: boolean
  isSuperAdmin?: boolean
  targetCompanyId?: string | null
}) {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState<Record<string,string>>({ label:'', value:'', name:'' })
  const [editId, setEditId] = useState<string|null>(null)
  const [editForm, setEditForm] = useState<Record<string,string>>({})
  const [loading, setLoading] = useState(false)

  const params: Record<string, string> = isSuperAdmin && targetCompanyId !== undefined
    ? { targetCompanyId: targetCompanyId ?? '' }
    : {}

  const { data = [] } = useQuery<LookupItem[]>({
    queryKey: [endpoint, targetCompanyId],
    queryFn: () => api.get(`/masterdata/${endpoint}`, { params }).then(r => r.data),
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: [endpoint] })

  const doCreate = async () => {
    const body: any = { ...form }
    if (isSuperAdmin && targetCompanyId !== undefined) body.targetCompanyId = targetCompanyId ?? ''
    setLoading(true)
    try {
      await api.post(`/masterdata/${endpoint}`, body)
      invalidate(); setAdding(false); setForm({ label:'', value:'', name:'' })
    } catch (e: any) { alert(e.response?.data?.message || e.message) }
    finally { setLoading(false) }
  }

  const doUpdate = async (id: string) => {
    setLoading(true)
    try {
      await api.put(`/masterdata/${endpoint}/${id}`, editForm)
      invalidate(); setEditId(null)
    } catch (e: any) { alert(e.response?.data?.message || e.message) }
    finally { setLoading(false) }
  }

  const doDelete = async (id: string) => {
    if (!confirm('ลบรายการนี้?')) return
    try {
      await api.delete(`/masterdata/${endpoint}/${id}`)
      invalidate()
    } catch (e: any) { alert(e.response?.data?.message || e.message) }
  }

  const ic = "border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"

  const isGlobalMode = isSuperAdmin && targetCompanyId === null
  const ownItems = (data as any[]).filter(item => !item.isGlobal)
  const globalItems = (data as any[]).filter(item => item.isGlobal)

  const editFormJsx = (id: string) => (
    <div className="bg-yellow-50 rounded-xl p-3 flex flex-wrap gap-2 items-center">
      {hasValue ? (
        <>
          <input value={editForm.label||''} onChange={e => setEditForm(p=>({...p,label:e.target.value}))} className={ic} placeholder="ชื่อแสดงผล"/>
          <input value={editForm.value||''} onChange={e => setEditForm(p=>({...p,value:e.target.value}))} className={ic} placeholder="ค่า (value)"/>
        </>
      ) : (
        <input value={editForm.name||''} onChange={e => setEditForm(p=>({...p,name:e.target.value}))} className={ic} placeholder="ชื่อ..."/>
      )}
      <button onClick={() => doUpdate(id)} disabled={loading} className={ic + ' bg-green-600 text-white border-green-600 px-3'}><Check size={14}/></button>
      <button onClick={() => setEditId(null)} className={ic}><X size={14}/></button>
    </div>
  )

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="font-semibold text-gray-700 flex items-center gap-2">
          {icon} {label}
          {isGlobalMode && <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">ค่าเริ่มต้น</span>}
        </div>
        {isAdmin && <button onClick={() => setAdding(!adding)} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"><Plus size={14}/> เพิ่ม</button>}
      </div>

      {adding && (
        <div className="bg-blue-50 rounded-xl p-3 flex flex-wrap gap-2 items-center">
          {hasValue ? (
            <>
              <input value={form.label} onChange={e => setForm(p=>({...p,label:e.target.value}))} className={ic} placeholder="ชื่อแสดงผล เช่น Desktop"/>
              <input value={form.value} onChange={e => setForm(p=>({...p,value:e.target.value}))} className={ic} placeholder="ค่า (value) เช่น DESKTOP"/>
            </>
          ) : (
            <input value={form.name} onChange={e => setForm(p=>({...p,name:e.target.value}))} className={ic} placeholder={`ชื่อ ${label}...`}/>
          )}
          <button onClick={doCreate} disabled={loading} className={ic + ' bg-blue-600 text-white border-blue-600 px-3'}><Check size={14}/></button>
          <button onClick={() => { setAdding(false); setForm({label:'',value:'',name:''}) }} className={ic}><X size={14}/></button>
        </div>
      )}

      {/* Global edit mode: SA managing global defaults */}
      {isGlobalMode && (
        <>
          {data.length === 0 && !adding && <div className="text-center text-gray-400 py-6 text-sm">ยังไม่มีข้อมูล</div>}
          {(data as any[]).map((item: any) => editId === item.id ? (
            <div key={item.id}>{editFormJsx(item.id)}</div>
          ) : (
            <div key={item.id} className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-2.5 flex items-center gap-3">
              <Globe size={12} className="text-purple-400 flex-shrink-0"/>
              <span className="flex-1 font-medium text-gray-800 text-sm">{item.label || item.name}</span>
              {item.value && <span className="text-xs font-mono bg-gray-100 text-gray-500 px-2 py-0.5 rounded">{item.value}</span>}
              {isAdmin && <button onClick={() => { setEditId(item.id); setEditForm({ label:item.label||'', value:item.value||'', name:item.name||'' }) }} className="p-1 text-gray-400 hover:text-blue-500"><Edit2 size={14}/></button>}
              {isAdmin && <button onClick={() => doDelete(item.id)} className="p-1 text-gray-400 hover:text-red-500"><Trash2 size={14}/></button>}
            </div>
          ))}
        </>
      )}

      {/* Company/normal view: own items editable, globals read-only */}
      {!isGlobalMode && (
        <>
          {ownItems.length === 0 && globalItems.length === 0 && !adding && (
            <div className="text-center text-gray-400 py-6 text-sm">ยังไม่มีข้อมูล</div>
          )}
          {ownItems.map((item: any) => editId === item.id ? (
            <div key={item.id}>{editFormJsx(item.id)}</div>
          ) : (
            <div key={item.id} className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-2.5 flex items-center gap-3">
              <span className="flex-1 font-medium text-gray-800 text-sm">{item.label || item.name}</span>
              {item.value && <span className="text-xs font-mono bg-gray-100 text-gray-500 px-2 py-0.5 rounded">{item.value}</span>}
              {isAdmin && <button onClick={() => { setEditId(item.id); setEditForm({ label:item.label||'', value:item.value||'', name:item.name||'' }) }} className="p-1 text-gray-400 hover:text-blue-500"><Edit2 size={14}/></button>}
              {isAdmin && <button onClick={() => doDelete(item.id)} className="p-1 text-gray-400 hover:text-red-500"><Trash2 size={14}/></button>}
            </div>
          ))}

          {globalItems.length > 0 && (
            <>
              {ownItems.length > 0 && (
                <div className="px-2 py-1.5"><p className="text-xs text-gray-400 font-medium">ค่าเริ่มต้นระบบ (อ่านอย่างเดียว)</p></div>
              )}
              {globalItems.map((item: any) => (
                <div key={item.id} className="bg-purple-50/30 rounded-xl border border-purple-100 px-4 py-2.5 flex items-center gap-3 opacity-80">
                  <span className="flex-1 font-medium text-gray-700 text-sm">{item.label || item.name}</span>
                  {item.value && <span className="text-xs font-mono bg-gray-100 text-gray-500 px-2 py-0.5 rounded">{item.value}</span>}
                  <GlobalBadge/>
                </div>
              ))}
            </>
          )}
        </>
      )}
    </div>
  )
}

const TABS: { key: TabKey; label: string }[] = [
  { key:'companies',       label:'🏢 Company' },
  { key:'asset-types',     label:'📋 ประเภท' },
  { key:'asset-statuses',  label:'🔵 สถานะ' },
  { key:'asset-categories',label:'📁 หมวดหมู่' },
  { key:'brands',          label:'🏷 ยี่ห้อ' },
  { key:'vendors',         label:'Vendor' },
  { key:'ma-templates',    label:'🔧 MA Templates' },
]

const ASSET_TABS: TabKey[] = ['asset-types', 'asset-statuses', 'asset-categories', 'brands']

export default function MasterDataPage({ isAdmin }: { isAdmin: boolean }) {
  const [tab, setTab] = useState<TabKey>('companies')
  const [targetCompanyId, setTargetCompanyId] = useState<string | null>(null)
  const qc = useQueryClient()

  const cu = getCurrentUser()
  const isSuperAdmin = cu?.role === 'SUPER_ADMIN'

  const { data: companies = [] } = useQuery<CompanyItem[]>({
    queryKey: ['companies'],
    queryFn: () => api.get('/masterdata/companies').then(r => r.data),
  })

  const isAssetTab = ASSET_TABS.includes(tab)
  const activeCompany = typeof targetCompanyId === 'string'
    ? (companies as any[]).find((c: any) => c.id === targetCompanyId)
    : null

  const handleSeedDone = () => {
    ASSET_TABS.forEach(k => qc.invalidateQueries({ queryKey: [k] }))
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Master Data</h1>
        {isSuperAdmin && isAssetTab && (
          <div className="flex flex-col items-end gap-1">
            <CompanyPicker companies={companies} selected={targetCompanyId} onChange={setTargetCompanyId}/>
            {targetCompanyId === null && (
              <p className="text-xs text-purple-500">ตั้งค่าเริ่มต้นที่ใช้กับทุกบริษัท</p>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 flex-wrap">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${tab===t.key?'bg-white shadow text-blue-700':'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Seed banner: shown when SA is viewing a specific company on asset tabs */}
      {isSuperAdmin && isAssetTab && activeCompany && (
        <AssetSeedBanner companyId={activeCompany.id} companyName={activeCompany.name} onDone={handleSeedDone}/>
      )}

      {tab === 'companies'        && <CompanyManager isAdmin={isAdmin}/>}
      {tab === 'asset-types'      && <LookupManager endpoint="asset-types"      label="ประเภท Asset"   icon={<Tag size={15}/>}       hasValue isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} targetCompanyId={isSuperAdmin ? targetCompanyId : undefined}/>}
      {tab === 'asset-statuses'   && <LookupManager endpoint="asset-statuses"   label="สถานะ Asset"    icon={<CircleDot size={15}/>} hasValue isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} targetCompanyId={isSuperAdmin ? targetCompanyId : undefined}/>}
      {tab === 'asset-categories' && <LookupManager endpoint="asset-categories" label="หมวดหมู่ Asset" icon={<FolderOpen size={15}/>}          isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} targetCompanyId={isSuperAdmin ? targetCompanyId : undefined}/>}
      {tab === 'brands'           && <SimpleList endpoint="brands" label="🏷 ยี่ห้อ (Brand)" isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} targetCompanyId={isSuperAdmin ? targetCompanyId : undefined} fields={[{key:'notes',placeholder:'หมายเหตุ...',label:'หมายเหตุ'}]}/>}
      {tab === 'vendors'          && <SimpleList endpoint="vendors" apiPath="/vendors" label="Vendor" fields={[{key:'contactName',placeholder:'ผู้ติดต่อ...',label:'ผู้ติดต่อ'},{key:'phone',placeholder:'เบอร์...',label:'เบอร์'},{key:'email',placeholder:'email...',label:'Email'}]}/>}
      {tab === 'ma-templates'     && <MaTemplateManager isAdmin={isAdmin}/>}
    </div>
  )
}
